import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  BarChart3, Eye, MousePointer, SkipForward, Clock, Play,
  TrendingUp, TrendingDown, Percent, RefreshCw, Loader2,
  ChevronRight, X, Calendar, Target, CheckCircle
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

const AdAnalytics = ({ authToken }) => {
  const [loading, setLoading] = useState(true);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [selectedAd, setSelectedAd] = useState(null);
  const [adDetails, setAdDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/api/vod/admin/ads/analytics`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setAnalyticsData(res.data);
    } catch (err) {
      console.error('Error fetching ad analytics:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAdDetails = async (adId) => {
    setLoadingDetails(true);
    try {
      const res = await axios.get(`${API}/api/vod/admin/ads/analytics/${adId}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setAdDetails(res.data);
    } catch (err) {
      console.error('Error fetching ad details:', err);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleAdClick = (ad) => {
    setSelectedAd(ad);
    fetchAdDetails(ad.ad_id);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-400 mb-4">{error}</p>
        <Button onClick={fetchAnalytics} className="bg-purple-600 hover:bg-purple-500">
          <RefreshCw className="h-4 w-4 mr-2" />
          Retry
        </Button>
      </div>
    );
  }

  const { summary, ads } = analyticsData || { summary: {}, ads: [] };

  return (
    <div className="space-y-8" data-testid="ad-analytics-section">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <BarChart3 className="h-6 w-6 text-purple-400" />
          <h2 className="text-xl font-bold">Ad Analytics</h2>
        </div>
        <Button onClick={fetchAnalytics} variant="outline" size="sm" className="border-white/20">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard
          icon={<Eye className="h-5 w-5" />}
          label="Total Impressions"
          value={summary.total_impressions?.toLocaleString() || 0}
          color="purple"
          data-testid="total-impressions-card"
        />
        <SummaryCard
          icon={<CheckCircle className="h-5 w-5" />}
          label="Completions"
          value={summary.total_completions?.toLocaleString() || 0}
          subtext={`${summary.overall_completion_rate || 0}% completion rate`}
          color="green"
        />
        <SummaryCard
          icon={<SkipForward className="h-5 w-5" />}
          label="Skips"
          value={summary.total_skips?.toLocaleString() || 0}
          subtext={`${summary.overall_skip_rate || 0}% skip rate`}
          color="orange"
        />
        <SummaryCard
          icon={<MousePointer className="h-5 w-5" />}
          label="Clicks"
          value={summary.total_clicks?.toLocaleString() || 0}
          subtext={`${summary.overall_ctr || 0}% CTR`}
          color="blue"
        />
      </div>

      {/* Performance Insights */}
      {(summary.best_performing_ad || summary.worst_performing_ad) && (
        <div className="grid md:grid-cols-2 gap-4">
          {summary.best_performing_ad && (
            <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-green-400" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Best Performing</p>
                <p className="font-medium text-green-400">{summary.best_performing_ad}</p>
              </div>
            </div>
          )}
          {summary.worst_performing_ad && summary.worst_performing_ad !== summary.best_performing_ad && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/20 flex items-center justify-center">
                <TrendingDown className="h-5 w-5 text-red-400" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Needs Improvement</p>
                <p className="font-medium text-red-400">{summary.worst_performing_ad}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Individual Ad Performance */}
      <div className="bg-white/5 rounded-2xl border border-white/10 overflow-hidden">
        <div className="p-4 border-b border-white/10">
          <h3 className="font-bold">Ad Performance</h3>
          <p className="text-sm text-gray-400">Click on an ad to view detailed analytics</p>
        </div>

        {ads.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <BarChart3 className="h-16 w-16 mx-auto mb-4 opacity-50" />
            <p>No analytics data yet</p>
            <p className="text-sm mt-2">Analytics will appear once your ads start playing</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left p-4 text-gray-400 text-sm font-medium">Ad</th>
                  <th className="text-center p-4 text-gray-400 text-sm font-medium">Impressions</th>
                  <th className="text-center p-4 text-gray-400 text-sm font-medium">Starts</th>
                  <th className="text-center p-4 text-gray-400 text-sm font-medium">Completions</th>
                  <th className="text-center p-4 text-gray-400 text-sm font-medium">Skips</th>
                  <th className="text-center p-4 text-gray-400 text-sm font-medium">Clicks</th>
                  <th className="text-center p-4 text-gray-400 text-sm font-medium">CTR</th>
                  <th className="text-center p-4 text-gray-400 text-sm font-medium">Avg Watch</th>
                  <th className="p-4"></th>
                </tr>
              </thead>
              <tbody>
                {ads.map((ad) => (
                  <tr
                    key={ad.ad_id}
                    className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors"
                    onClick={() => handleAdClick(ad)}
                    data-testid={`ad-row-${ad.ad_id}`}
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="w-16 h-10 rounded bg-gray-800 overflow-hidden flex-shrink-0">
                          {ad.thumbnail_url ? (
                            <img src={ad.thumbnail_url} alt={ad.ad_title} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Play className="h-4 w-4 text-gray-600" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium truncate max-w-[200px]">{ad.ad_title}</p>
                          <p className="text-xs text-gray-500">{ad.duration ? `${ad.duration}s` : 'N/A'}</p>
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-center">
                      <span className="font-medium">{ad.total_impressions}</span>
                    </td>
                    <td className="p-4 text-center text-gray-400">
                      {ad.total_starts}
                    </td>
                    <td className="p-4 text-center">
                      <div className="flex flex-col items-center">
                        <span className="font-medium text-green-400">{ad.total_completions}</span>
                        <span className="text-xs text-gray-500">{ad.completion_rate}%</span>
                      </div>
                    </td>
                    <td className="p-4 text-center">
                      <div className="flex flex-col items-center">
                        <span className="font-medium text-orange-400">{ad.total_skips}</span>
                        <span className="text-xs text-gray-500">{ad.skip_rate}%</span>
                      </div>
                    </td>
                    <td className="p-4 text-center">
                      <span className="font-medium text-blue-400">{ad.total_clicks}</span>
                    </td>
                    <td className="p-4 text-center">
                      <span className={`font-medium ${
                        ad.click_through_rate >= 2 ? 'text-green-400' :
                        ad.click_through_rate >= 1 ? 'text-yellow-400' :
                        'text-gray-400'
                      }`}>
                        {ad.click_through_rate}%
                      </span>
                    </td>
                    <td className="p-4 text-center text-gray-400">
                      {ad.average_watch_time > 0 ? `${ad.average_watch_time}s` : '-'}
                    </td>
                    <td className="p-4">
                      <ChevronRight className="h-4 w-4 text-gray-500" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Funnel Visualization */}
      {ads.length > 0 && (
        <div className="bg-white/5 rounded-2xl border border-white/10 p-6">
          <h3 className="font-bold mb-4 flex items-center gap-2">
            <Target className="h-5 w-5 text-purple-400" />
            Ad Funnel Overview
          </h3>
          <FunnelVisualization
            impressions={summary.total_impressions || 0}
            starts={ads.reduce((sum, a) => sum + a.total_starts, 0)}
            completions={summary.total_completions || 0}
            clicks={summary.total_clicks || 0}
          />
        </div>
      )}

      {/* Ad Detail Modal */}
      {selectedAd && (
        <AdDetailModal
          ad={selectedAd}
          details={adDetails}
          loading={loadingDetails}
          onClose={() => { setSelectedAd(null); setAdDetails(null); }}
        />
      )}
    </div>
  );
};

// Summary Card Component
const SummaryCard = ({ icon, label, value, subtext, color }) => {
  const colors = {
    purple: 'from-purple-900/50 to-purple-800/30 border-purple-500/30 text-purple-400',
    green: 'from-green-900/50 to-green-800/30 border-green-500/30 text-green-400',
    orange: 'from-orange-900/50 to-orange-800/30 border-orange-500/30 text-orange-400',
    blue: 'from-blue-900/50 to-blue-800/30 border-blue-500/30 text-blue-400',
  };

  return (
    <div className={`bg-gradient-to-br ${colors[color]} rounded-xl p-5 border`}>
      <div className="flex items-center gap-3 mb-2">
        <div className={`w-10 h-10 rounded-lg bg-${color}-500/20 flex items-center justify-center ${colors[color].split(' ').pop()}`}>
          {icon}
        </div>
        <span className="text-gray-400 text-sm">{label}</span>
      </div>
      <p className="text-3xl font-bold">{value}</p>
      {subtext && <p className="text-xs text-gray-500 mt-1">{subtext}</p>}
    </div>
  );
};

// Funnel Visualization Component
const FunnelVisualization = ({ impressions, starts, completions, clicks }) => {
  const steps = [
    { label: 'Impressions', value: impressions, color: 'bg-purple-500' },
    { label: 'Started', value: starts, color: 'bg-blue-500' },
    { label: 'Completed', value: completions, color: 'bg-green-500' },
    { label: 'Clicked', value: clicks, color: 'bg-pink-500' },
  ];

  const maxValue = Math.max(...steps.map(s => s.value), 1);

  return (
    <div className="space-y-4">
      {steps.map((step, idx) => {
        const width = Math.max((step.value / maxValue) * 100, 5);
        const rate = idx > 0 && steps[idx - 1].value > 0 
          ? ((step.value / steps[idx - 1].value) * 100).toFixed(1)
          : null;
        
        return (
          <div key={step.label} className="flex items-center gap-4">
            <div className="w-24 text-sm text-gray-400">{step.label}</div>
            <div className="flex-1 relative">
              <div 
                className={`h-8 ${step.color} rounded-r-lg flex items-center justify-end pr-3 transition-all duration-500`}
                style={{ width: `${width}%` }}
              >
                <span className="text-white font-bold text-sm">{step.value.toLocaleString()}</span>
              </div>
            </div>
            <div className="w-16 text-right">
              {rate && (
                <span className={`text-sm ${
                  parseFloat(rate) >= 50 ? 'text-green-400' : 
                  parseFloat(rate) >= 25 ? 'text-yellow-400' : 'text-red-400'
                }`}>
                  {rate}%
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Ad Detail Modal Component
const AdDetailModal = ({ ad, details, loading, onClose }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-gray-900 rounded-2xl border border-white/10 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gray-900 p-6 border-b border-white/10 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-8 rounded bg-gray-800 overflow-hidden">
              {ad.thumbnail_url ? (
                <img src={ad.thumbnail_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Play className="h-4 w-4 text-gray-600" />
                </div>
              )}
            </div>
            <div>
              <h2 className="font-bold">{ad.ad_title}</h2>
              <p className="text-xs text-gray-500">Detailed Analytics</p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="h-6 w-6" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
          </div>
        ) : details ? (
          <div className="p-6 space-y-6">
            {/* Summary Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-gray-800 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold">{details.total_impressions}</p>
                <p className="text-xs text-gray-500">Impressions</p>
              </div>
              <div className="bg-gray-800 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-green-400">{details.completion_rate}%</p>
                <p className="text-xs text-gray-500">Completion Rate</p>
              </div>
              <div className="bg-gray-800 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-orange-400">{details.skip_rate}%</p>
                <p className="text-xs text-gray-500">Skip Rate</p>
              </div>
              <div className="bg-gray-800 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-blue-400">{details.ctr}%</p>
                <p className="text-xs text-gray-500">Click-Through Rate</p>
              </div>
            </div>

            {/* Quartile Progress */}
            <div>
              <h3 className="font-medium mb-3 flex items-center gap-2">
                <Clock className="h-4 w-4 text-purple-400" />
                Watch Progress (Quartiles)
              </h3>
              <div className="bg-gray-800 rounded-lg p-4">
                <div className="flex items-center gap-2">
                  <div className="flex-1">
                    <div className="flex justify-between text-xs text-gray-500 mb-1">
                      <span>0%</span>
                      <span>25%</span>
                      <span>50%</span>
                      <span>75%</span>
                      <span>100%</span>
                    </div>
                    <div className="h-4 bg-gray-700 rounded-full overflow-hidden flex">
                      <div 
                        className="h-full bg-purple-500 transition-all"
                        style={{ width: `25%` }}
                        title={`Reached 25%: ${details.quartile_25 ?? ad.quartile_25 ?? 0}`}
                      />
                      <div 
                        className="h-full bg-blue-500 transition-all"
                        style={{ width: `25%` }}
                        title={`Reached 50%: ${details.quartile_50 ?? ad.quartile_50 ?? 0}`}
                      />
                      <div 
                        className="h-full bg-green-500 transition-all"
                        style={{ width: `25%` }}
                        title={`Reached 75%: ${details.quartile_75 ?? ad.quartile_75 ?? 0}`}
                      />
                      <div 
                        className="h-full bg-pink-500 transition-all"
                        style={{ width: `25%` }}
                        title={`Completed: ${details.total_completions}`}
                      />
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-2 mt-3 text-center text-sm">
                  <div>
                    <p className="font-medium text-purple-400">{details.quartile_25 ?? ad.quartile_25 ?? 0}</p>
                    <p className="text-xs text-gray-500">25%</p>
                  </div>
                  <div>
                    <p className="font-medium text-blue-400">{details.quartile_50 ?? ad.quartile_50 ?? 0}</p>
                    <p className="text-xs text-gray-500">50%</p>
                  </div>
                  <div>
                    <p className="font-medium text-green-400">{details.quartile_75 ?? ad.quartile_75 ?? 0}</p>
                    <p className="text-xs text-gray-500">75%</p>
                  </div>
                  <div>
                    <p className="font-medium text-pink-400">{details.total_completions}</p>
                    <p className="text-xs text-gray-500">100%</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Skip Time Distribution */}
            {details.skip_distribution && details.skip_distribution.length > 0 && (
              <div>
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <SkipForward className="h-4 w-4 text-orange-400" />
                  Skip Time Distribution
                </h3>
                <div className="space-y-2">
                  {details.skip_distribution.map((bucket) => (
                    <div key={bucket.time_range} className="flex items-center gap-3">
                      <span className="text-xs text-gray-500 w-16">{bucket.time_range}</span>
                      <div className="flex-1 h-4 bg-gray-800 rounded overflow-hidden">
                        <div 
                          className="h-full bg-orange-500 rounded"
                          style={{ width: `${bucket.percentage}%` }}
                        />
                      </div>
                      <span className="text-xs text-gray-400 w-16 text-right">
                        {bucket.count} ({bucket.percentage}%)
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Daily Stats Chart */}
            {details.daily_stats && details.daily_stats.length > 0 && (
              <div>
                <h3 className="font-medium mb-3 flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-blue-400" />
                  Last 30 Days Performance
                </h3>
                <div className="bg-gray-800 rounded-lg p-4">
                  <div className="flex items-end gap-1 h-24">
                    {details.daily_stats.slice(-14).map((day, idx) => {
                      const maxImpressions = Math.max(...details.daily_stats.map(d => d.impressions), 1);
                      const height = (day.impressions / maxImpressions) * 100;
                      return (
                        <div 
                          key={day.date} 
                          className="flex-1 flex flex-col items-center"
                          title={`${day.date}: ${day.impressions} impressions`}
                        >
                          <div 
                            className="w-full bg-purple-500 rounded-t transition-all"
                            style={{ height: `${height}%`, minHeight: day.impressions > 0 ? '4px' : '0' }}
                          />
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex justify-between mt-2 text-xs text-gray-500">
                    <span>{details.daily_stats.slice(-14)[0]?.date.slice(5)}</span>
                    <span>{details.daily_stats.slice(-1)[0]?.date.slice(5)}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Recent Events */}
            {details.recent_events && details.recent_events.length > 0 && (
              <div>
                <h3 className="font-medium mb-3">Recent Events</h3>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {details.recent_events.slice(0, 10).map((event, idx) => (
                    <div 
                      key={idx} 
                      className="flex items-center justify-between text-sm bg-gray-800 rounded-lg px-3 py-2"
                    >
                      <div className="flex items-center gap-2">
                        <Badge className={
                          event.event_type === 'complete' ? 'bg-green-500' :
                          event.event_type === 'skip' ? 'bg-orange-500' :
                          event.event_type === 'click' ? 'bg-blue-500' :
                          event.event_type === 'impression' ? 'bg-purple-500' :
                          'bg-gray-500'
                        }>
                          {event.event_type}
                        </Badge>
                        {event.ad_break && (
                          <span className="text-xs text-gray-500">Break {event.ad_break}</span>
                        )}
                      </div>
                      <div className="text-right">
                        {event.watch_duration && (
                          <span className="text-xs text-gray-400 mr-2">
                            {event.watch_duration.toFixed(1)}s watched
                          </span>
                        )}
                        <span className="text-xs text-gray-500">
                          {event.created_at ? new Date(event.created_at).toLocaleString() : 'N/A'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="p-6 text-center text-gray-500">
            No detailed data available
          </div>
        )}
      </div>
    </div>
  );
};

export default AdAnalytics;
